import { getHoseCodesForAtomsAsFragments } from '../hose/getHoseCodesForAtomsAsFragments.js';
import { getAllAtomsPaths } from "../path/getAllAtomsPaths.js";
import { ensureMapNo } from "../util/ensureMapNo.js";
import { getCompactCopyWithoutCustomLabels } from "../util/getCompactCopyWithoutCustomLabels.js";
import { getConnectivityMatrix } from '../util/getConnectivityMatrix.js';
import { tagAtom } from "../util/tagAtom.js";
import { getCanonizedDiaIDs } from "./getCanonizedDiaIDs.js";
import { getCanonizedHoseCodes } from "./getCanonizedHoseCodes.js";
import { getCanonizedHoseCodesForPath } from "./getCanonizedHoseCodesForPaths.js";
import { getDiaIDsAndInfo } from "./getDiaIDsAndInfo.js";
import { getEnantioIDs } from "./getEnantioIDs.js";
import { getFinalRanks, getHeterotopicSymmetryRanks, } from "./getHeterotopicSymmetryRanks.js";
import { getMoleculeWithH } from "./getMoleculeWithH.js";
import { getXMolecule } from "./getXMolecule.js";
/**
 * This class deals with topicity information and hose codes
 * It is optimized to avoid recalculation of the same information
 */
export class TopicMolecule {
    originalMolecule;
    molecule;
    idCode;
    options;
    cache;
    constructor(molecule, options = {}) {
        this.originalMolecule = molecule;
        this.options = {
            maxPathLength: 5,
            maxNbAtoms: 250,
            logger: console,
            ...options,
        };
        this.idCode = molecule.getIDCode();
        this.molecule = this.originalMolecule.getCompactCopy();
        this.molecule.ensureHelperArrays(molecule.getOCL().Molecule.cHelperNeighbours);
        this.molecule.ensureHelperArrays(this.molecule.getOCL().Molecule.cHelperNeighbours);
        this.cache = {};
    }
    /**
     * This method ensures that all the atoms have a mapNo corresponding to the atom number.
     * It will enforce mapNo in molecule and moleculeWithH
     * We start numbering the atoms at 1
     */
    setAtomNoInMapNo() {
        const molecules = [this.molecule, this.moleculeWithH];
        for (const molecule of molecules) {
            for (let i = 0; i < molecule.getAllAtoms(); i++) {
                molecule.setAtomMapNo(i, i + 1, false);
            }
        }
    }
    /**
     * This method ensures that all the atoms have a mapNo in the molecule (and not the moleculeWithH! )
     */
    ensureMapNo() {
        ensureMapNo(this.molecule);
    }
    /**
     * For each atom we will return an array of objects that contains the
     * different possible path as well as the canonic hose codes
     * @param options
     * @returns
     */
    getHoseCodesForPath(options = {}) {
        return getCanonizedHoseCodesForPath(this, options);
    }
    /**
     * Return one fragment for a specific sphere size and specific root atoms
     * @param rootAtoms
     * @param options
     * @returns
     */
    getHoseFragment(rootAtoms, options = {}) {
        const { sphereSize = 2, tagAtoms = rootAtoms, tagAtomFct } = options;
        const fragments = getHoseCodesForAtomsAsFragments(this.moleculeWithH, {
            rootAtoms,
            minSphereSize: sphereSize,
            maxSphereSize: sphereSize,
            tagAtoms,
            tagAtomFct,
        });
        return fragments[0];
    }
    getAtomPathsFrom(atom, options = {}) {
        const { minPathLength = 1, maxPathLength = this.options.maxPathLength, toAtomicNo, } = options;
        if (maxPathLength > this.options.maxPathLength) {
            throw new Error('The maxPathLength is too long, you should increase the maxPathLength when instantiating the TopicMolecule');
        }
        const atomPaths = this.atomsPaths[atom];
        if (!atomPaths) {
            throw new Error('Unexpected missing atom path');
        }
        const paths = [];
        for (let i = minPathLength; i <= maxPathLength; i++) {
            const atomPathValue = atomPaths[i];
            if (!atomPathValue) {
                throw new Error(`Unexpected missing atom path at index ${i}`);
            }
            for (const atomPath of atomPathValue) {
                if (!toAtomicNo ||
                    this.moleculeWithH.getAtomicNo(atomPath.path.at(-1)) ===
                        toAtomicNo) {
                    paths.push(atomPath.path);
                }
            }
        }
        return paths;
    }
    getAtomPaths(atom1, atom2, options = {}) {
        const { pathLength } = options;
        if (pathLength !== undefined && pathLength > this.options.maxPathLength) {
            throw new Error('The distance is too long, you should increase the maxPathLength when instantiating the TopicMolecule');
        }
        const atomPaths = this.atomsPaths[atom1];
        if (!atomPaths) {
            throw new Error('Unexpected missing atom path');
        }
        const minDistance = pathLength || 0;
        const maxDistance = pathLength || this.options.maxPathLength;
        const paths = [];
        for (let i = minDistance; i <= maxDistance; i++) {
            const atomPathValue = atomPaths[i];
            if (!atomPathValue) {
                throw new Error(`Unexpected missing atom path at index ${i}`);
            }
            for (const atomPath of atomPathValue) {
                if (atomPath.path.at(-1) === atom2) {
                    paths.push(atomPath.path);
                }
            }
        }
        return paths;
    }
    get atomsPaths() {
        if (this.cache.atomsPaths)
            return this.cache.atomsPaths;
        this.cache.atomsPaths = getAllAtomsPaths(this.moleculeWithH, {
            maxPathLength: this.options.maxPathLength,
        });
        return this.cache.atomsPaths;
    }
    toMolfile(options = {}) {
        const { version = 2 } = options;
        if (version === 2) {
            return this.molecule.toMolfile();
        }
        return this.molecule.toMolfileV3();
    }
    getMolecule() {
        return this.molecule;
    }
    /**
     * Returns a new TopicMolecule but will copy precalculated information
     * if possible (same idCode). This is very practical when expanding hydrogens
     * for example.
     * @param molecule
     * @returns
     */
    fromMolecule(molecule) {
        const idCode = molecule.getIDCode();
        if (idCode !== this.idCode) {
            // no way for optimisation
            return new TopicMolecule(molecule);
        }
        const topicMolecule = new TopicMolecule(molecule);
        topicMolecule.cache = {
            canonizedDiaIDs: this.cache.canonizedDiaIDs,
            canonizedHoseCodes: this.cache.canonizedHoseCodes,
            prochiralityByEnantioID: this.cache.prochiralityByEnantioID,
        };
        return topicMolecule;
    }
    /**
     * Returns a molecule with all the hydrogens added. The order is NOT canonized
     */
    get moleculeWithH() {
        if (this.cache.moleculeWithH)
            return this.cache.moleculeWithH;
        this.cache.moleculeWithH = getMoleculeWithH(this.molecule, {
            maxNbAtoms: this.options.maxNbAtoms,
            logger: this.options.logger,
        });
        return this.cache.moleculeWithH;
    }
    get xMolecule() {
        if (this.cache.xMolecule)
            return this.cache.xMolecule;
        this.cache.xMolecule = getXMolecule(this.moleculeWithH);
        return this.cache.xMolecule;
    }
    /**
     * This is related to the current moleculeWithH. The order is NOT canonized
     */
    get diaIDs() {
        if (this.cache.diaIDs)
            return this.cache.diaIDs;
        const diaIDs = [];
        if (this.moleculeWithH.getAllAtoms() > this.options.maxNbAtoms) {
            this.options.logger.warn(`too many atoms to evaluate heterotopicity: ${this.moleculeWithH.getAllAtoms()} > ${this.options.maxNbAtoms}`);
        }
        else {
            for (let i = 0; i < this.moleculeWithH.getAllAtoms(); i++) {
                diaIDs.push(this.canonizedDiaIDs[this.finalRanks[i]]);
            }
        }
        this.cache.diaIDs = diaIDs;
        return diaIDs;
    }
    /**
     * Computes a canonical ID for each atom in `moleculeWithH` that encodes
     * absolute stereochemistry (no `makeRacemic` step). Unlike `diaIDs`,
     * these IDs differ between enantiomers and are used as cache keys for
     * pro-R / pro-S assignment so that each enantiomer gets an independent
     * computation.
     */
    get enantioIDs() {
        if (this.cache.enantioIDs)
            return this.cache.enantioIDs;
        this.cache.enantioIDs =
            this.moleculeWithH.getAllAtoms() <= this.options.maxNbAtoms
                ? getEnantioIDs(this)
                : [];
        return this.cache.enantioIDs;
    }
    /**
     * We return the atomIDs corresponding to the specified diaID as well has the attached hydrogens or heavy atoms
     * @param diaID
     * @returns
     */
    getDiaIDsObject() {
        return groupDiastereotopicAtomIDsAsObject(this.diaIDs, this.molecule, this.moleculeWithH);
    }
    /**
     * This is related to the current moleculeWithH. The order is NOT canonized
     */
    get hoseCodes() {
        if (this.cache.hoseCodes)
            return this.cache.hoseCodes;
        const hoseCodes = [];
        for (let i = 0; i < this.moleculeWithH.getAllAtoms(); i++) {
            hoseCodes.push(this.canonizedHoseCodes[this.finalRanks[i]]);
        }
        this.cache.hoseCodes = hoseCodes;
        return hoseCodes;
    }
    get canonizedDiaIDs() {
        if (this.cache.canonizedDiaIDs)
            return this.cache.canonizedDiaIDs;
        if (this.moleculeWithH.getAllAtoms() > this.options.maxNbAtoms) {
            this.options.logger.warn(`too many atoms to evaluate heterotopic chiral bonds: ${this.moleculeWithH.getAllAtoms()} > ${this.options.maxNbAtoms}`);
            this.cache.canonizedDiaIDs = [];
            return [];
        }
        this.cache.canonizedDiaIDs = getCanonizedDiaIDs(this);
        return this.cache.canonizedDiaIDs;
    }
    get canonizedHoseCodes() {
        if (this.cache.canonizedHoseCodes) {
            return this.cache.canonizedHoseCodes;
        }
        this.cache.canonizedHoseCodes = getCanonizedHoseCodes(this);
        return this.cache.canonizedHoseCodes;
    }
    /**
     * Returns the distance matrix for the current moleculeWithH
     */
    get distanceMatrix() {
        return getConnectivityMatrix(this.moleculeWithH, { pathLength: true });
    }
    get diaIDsAndInfo() {
        if (this.cache.diaIDsAndInfo) {
            return this.cache.diaIDsAndInfo;
        }
        this.cache.diaIDsAndInfo = getDiaIDsAndInfo(this, this.canonizedDiaIDs);
        return this.cache.diaIDsAndInfo;
    }
    /**
     * Returns symmetryRanks for all the atoms including hydrogens. Those ranks
     * deals with topicity and is related to the current moleculeWithH.
     * In order to calculate the ranks we replace all the
     * hydrogens with a X atom.
     */
    get heterotopicSymmetryRanks() {
        if (this.cache.heterotopicSymmetryRanks) {
            return this.cache.heterotopicSymmetryRanks;
        }
        this.cache.heterotopicSymmetryRanks = getHeterotopicSymmetryRanks(this.xMolecule);
        return [...this.cache.heterotopicSymmetryRanks];
    }
    /**
     * Returns finalRanks for all the atoms including hydrogens. Those ranks
     * deals with topicity and is related to the current moleculeWithH.
     * All the atoms have a unique identifier.j
     * In order to calculate the ranks we replace all the
     * hydrogens with a X atom.
     */
    get finalRanks() {
        if (this.cache.finalRanks)
            return this.cache.finalRanks;
        this.cache.finalRanks = getFinalRanks(this.xMolecule);
        return this.cache.finalRanks;
    }
    toMolfileWithH(options = {}) {
        const { version = 2 } = options;
        if (version === 2) {
            return this.moleculeWithH.toMolfile();
        }
        return this.moleculeWithH.toMolfileV3();
    }
    toMolfileWithoutH(options = {}) {
        const molecule = this.molecule.getCompactCopy();
        molecule.ensureHelperArrays(molecule.getOCL().Molecule.cHelperNeighbours);
        const atomsToDelete = [];
        for (let atomID = 0; atomID < molecule.getAtoms(); atomID++) {
            if (molecule.getExplicitHydrogens(atomID) > 0) {
                for (let i = 0; i < molecule.getAllConnAtoms(atomID); i++) {
                    const connectedAtom = molecule.getConnAtom(atomID, i);
                    if (molecule.getAtomicNo(connectedAtom) === 1) {
                        atomsToDelete.push(connectedAtom);
                    }
                }
            }
        }
        molecule.deleteAtoms(atomsToDelete);
        const { version = 2 } = options;
        if (version === 2) {
            return molecule.toMolfile();
        }
        return molecule.toMolfileV3();
    }
    /**
     * Returns an array of objects containing the oclID and the corresponding hydrogens and atoms
     * for the specified atomLabel (if any)
     * This always applied to the molecule with expanded hydrogens and chirality
     * @param options
     * @returns
     */
    getGroupedDiastereotopicAtomIDs(options = {}) {
        if (!this.diaIDs)
            return undefined;
        return groupDiastereotopicAtomIDs(this.diaIDs, this.moleculeWithH, options);
    }
    /**
     * Returns an array, indexed by atom number in `moleculeWithH`, holding the
     * pro-R / pro-S descriptor (`'r'` or `'s'`) for every hydrogen of a CH2
     * whose two hydrogens are diastereotopic. Atoms that are not labelled
     * prochiral hydrogens hold `undefined`. The underlying hash
     * (`prochiralityByEnantioID`) is keyed by `enantioID`, which encodes
     * absolute stereochemistry, so two enantiomers receive independent
     * assignments. Hydrogens are never R or S themselves (that is a
     * stereocenter descriptor), so the label is always lowercase regardless
     * of whether the prochiral carbon is a true or a pseudoasymmetric centre.
     * The assignment is determined by replacing one H with a higher-priority
     * pseudo-atom (via `tagAtom`) and reading `getAtomCIPParity` of the carbon.
     * @returns array of `'r'` / `'s'` / `undefined` indexed by atom number
     */
    get prochiralities() {
        if (this.cache.prochiralities)
            return this.cache.prochiralities;
        const map = this.prochiralityByEnantioID;
        const enantioIDs = this.enantioIDs;
        const result = new Array(enantioIDs.length);
        for (let atom = 0; atom < enantioIDs.length; atom++) {
            result[atom] = map[enantioIDs[atom]];
        }
        this.cache.prochiralities = result;
        return result;
    }
    /**
     * Hash from `enantioID` to the pro-R / pro-S descriptor (`'r'` or `'s'`).
     * Only entries for hydrogens that actually carry a prochiral descriptor
     * are present. Keyed by absolute IDs (no `makeRacemic`), so enantiomers
     * get independent entries. Transferred across instances by `fromMolecule()`.
     * @returns map from enantioID string to `'r'` or `'s'`
     */
    get prochiralityByEnantioID() {
        if (this.cache.prochiralityByEnantioID) {
            return this.cache.prochiralityByEnantioID;
        }
        this.cache.prochiralityByEnantioID = computeProchiralityByEnantioID(this);
        return this.cache.prochiralityByEnantioID;
    }
    /**
     * Set a pro-R / pro-S `customLabel` (lowercase `r` / `s`) on each prochiral
     * CH2 hydrogen, in both `molecule` and `moleculeWithH`. The label is prefixed
     * with `']'` so OCL renders it as a superscript, and appended to the
     * hydrogen's existing `customLabel` (or the parent carbon's). By default only
     * diastereotopic hydrogens are labelled; pass `includeEnantiotopic` to also
     * label enantiotopic ones. Idempotent and does no extra CIP work on repeat.
     * @param options - controls which prochiral hydrogens are labelled
     * @returns The number of hydrogens labelled in `moleculeWithH`
     */
    setProchiralHydrogenLabels(options = {}) {
        const { includeEnantiotopic = false } = options;
        const moleculeWithH = this.moleculeWithH;
        const molecule = this.molecule;
        const moleculeAtoms = molecule.getAllAtoms();
        const map = this.prochiralityByEnantioID;
        const enantioIDs = this.enantioIDs;
        const diaIDs = includeEnantiotopic ? null : this.diaIDs;
        let count = 0;
        for (let atom = 0; atom < enantioIDs.length; atom++) {
            const letter = map[enantioIDs[atom]];
            if (!letter)
                continue;
            if (diaIDs && !isDiastereotopicHydrogen(moleculeWithH, diaIDs, atom)) {
                continue;
            }
            appendProchiralLabel(moleculeWithH, atom, moleculeWithH.getConnAtom(atom, 0), letter);
            count++;
            if (atom < moleculeAtoms && molecule.getAtomicNo(atom) === 1) {
                appendProchiralLabel(molecule, atom, molecule.getConnAtom(atom, 0), letter);
            }
        }
        return count;
    }
    /**
     * Remove a trailing pro-R / pro-S character (`r` or `s`) from the
     * `customLabel` of every hydrogen that carries one, in both `molecule`
     * and `moleculeWithH`. A label left as just the superscript marker `']'`
     * is removed entirely. Idempotent. Hydrogens whose `customLabel` does
     * not end with `r` or `s` are left untouched.
     * @returns The number of labels that were stripped from `moleculeWithH`
     */
    removeProchiralHydrogenLabels() {
        const count = stripTrailingProchiralLetter(this.moleculeWithH);
        stripTrailingProchiralLetter(this.molecule);
        return count;
    }
    /**
     * This method returns a mapping between the diaIDs of the current molecule.
     * It expects that the initial molfile and the final molfile contains atomMapNo
     * in order to track which atom becomes which one.
     * @param molecule
     */
    getDiaIDsMapping(molecule) {
        const topicMolecule = new TopicMolecule(molecule);
        const originalDiaIDs = this.diaIDsAndInfo.filter((diaID) => diaID.atomMapNo);
        const destinationDiaIDs = topicMolecule.diaIDsAndInfo.filter((diaID) => diaID.atomMapNo);
        const mapping = {};
        // we first check all the atoms present in the molfile
        for (const destinationDiaID of destinationDiaIDs) {
            const originalDiaID = originalDiaIDs.find((diaID) => diaID.atomMapNo === destinationDiaID.atomMapNo);
            const newIDCode = destinationDiaID.idCode;
            const oldIDCode = originalDiaID.idCode;
            if (oldIDCode in mapping) {
                if (mapping[oldIDCode] !== newIDCode) {
                    mapping[oldIDCode] = undefined;
                }
            }
            else {
                mapping[oldIDCode] = newIDCode;
            }
        }
        // we now check all the attached hydrogens that are not defined in the molfile and were not yet mapped
        for (const destinationDiaID of destinationDiaIDs) {
            const originalDiaID = originalDiaIDs.find((diaID) => diaID.atomMapNo === destinationDiaID.atomMapNo);
            for (let i = 0; i < originalDiaID.attachedHydrogensIDCodes.length; i++) {
                const oldHydrogenIDCode = originalDiaID.attachedHydrogensIDCodes.at(i);
                if (mapping[oldHydrogenIDCode])
                    continue;
                const newHydrogenIDCode = destinationDiaID.attachedHydrogensIDCodes[i];
                if (oldHydrogenIDCode && newHydrogenIDCode) {
                    if (oldHydrogenIDCode in mapping) {
                        if (mapping[oldHydrogenIDCode] !== newHydrogenIDCode) {
                            mapping[oldHydrogenIDCode] = undefined;
                        }
                    }
                    else {
                        mapping[oldHydrogenIDCode] = newHydrogenIDCode;
                    }
                }
            }
        }
        return mapping;
    }
}
function computeProchiralityByEnantioID(topicMolecule) {
    const target = topicMolecule.moleculeWithH;
    const { Molecule } = target.getOCL();
    const enantioIDs = topicMolecule.enantioIDs;
    const result = {};
    for (let atom = 0; atom < target.getAllAtoms(); atom++) {
        if (target.getAtomicNo(atom) !== 6)
            continue;
        const hydrogens = [];
        let nonHydrogenCount = 0;
        for (let j = 0; j < target.getAllConnAtoms(atom); j++) {
            const connected = target.getConnAtom(atom, j);
            if (target.getAtomicNo(connected) === 1) {
                hydrogens.push(connected);
            }
            else {
                nonHydrogenCount++;
            }
        }
        if (hydrogens.length !== 2 || nonHydrogenCount !== 2)
            continue;
        const enantioID0 = enantioIDs[hydrogens[0]];
        const enantioID1 = enantioIDs[hydrogens[1]];
        if (enantioID0 === enantioID1)
            continue;
        if (result[enantioID0] && result[enantioID1])
            continue;
        const probe = getCompactCopyWithoutCustomLabels(target);
        tagAtom(probe, hydrogens[0]);
        probe.ensureHelperArrays(Molecule.cHelperCIP);
        const cipParity = probe.getAtomCIPParity(atom);
        if (cipParity === Molecule.cAtomCIPParityRorM) {
            result[enantioID0] = 'r';
            result[enantioID1] = 's';
        }
        else if (cipParity === Molecule.cAtomCIPParitySorP) {
            result[enantioID0] = 's';
            result[enantioID1] = 'r';
        }
    }
    return result;
}
// Two geminal CH2 hydrogens are diastereotopic when their (racemic) diaIDs
// differ; an equal diaID means they are enantiotopic (equivalent in an achiral
// environment) and should be skipped unless explicitly requested.
function isDiastereotopicHydrogen(molecule, diaIDs, hydrogen) {
    const carbon = molecule.getConnAtom(hydrogen, 0);
    for (let j = 0; j < molecule.getAllConnAtoms(carbon); j++) {
        const connected = molecule.getConnAtom(carbon, j);
        if (connected !== hydrogen && molecule.getAtomicNo(connected) === 1) {
            return diaIDs[hydrogen] !== diaIDs[connected];
        }
    }
    return false;
}
function appendProchiralLabel(molecule, hydrogen, parent, letter) {
    const existing = molecule.getAtomCustomLabel(hydrogen);
    if (existing) {
        molecule.setAtomCustomLabel(hydrogen, toSuperscript(existing.replace(/[rs]$/, '')) + letter);
        return;
    }
    const parentLabel = molecule.getAtomCustomLabel(parent);
    if (parentLabel) {
        molecule.setAtomCustomLabel(hydrogen, toSuperscript(parentLabel) + letter);
        return;
    }
    molecule.setAtomCustomLabel(hydrogen, `]${letter}`);
}
// OCL renders a custom label as a superscript at the top-left of the atom when
// it starts with ']'. Keep the prochiral descriptor (and any inherited prefix)
// superscript, matching the skeleton numbering produced by `applyFragmentLabels`.
function toSuperscript(label) {
    return label.startsWith(']') ? label : `]${label}`;
}
function stripTrailingProchiralLetter(molecule) {
    let count = 0;
    for (let atom = 0; atom < molecule.getAllAtoms(); atom++) {
        if (molecule.getAtomicNo(atom) !== 1)
            continue;
        const customLabel = molecule.getAtomCustomLabel(atom);
        if (!customLabel)
            continue;
        const stripped = customLabel.replace(/[rs]$/, '');
        if (stripped === customLabel)
            continue;
        molecule.setAtomCustomLabel(atom, stripped && stripped !== ']' ? stripped : null);
        count++;
    }
    return count;
}
export function groupDiastereotopicAtomIDs(diaIDs, molecule, options = {}) {
    const diaIDsObject = groupDiastereotopicAtomIDsAsObject(diaIDs, molecule, molecule, options);
    return Object.values(diaIDsObject);
}
function groupDiastereotopicAtomIDsAsObject(diaIDs, molecule, moleculeWithH, options = {}) {
    const { atomLabel } = options;
    const diaIDsObject = {};
    for (let i = 0; i < diaIDs.length; i++) {
        if (!atomLabel || moleculeWithH.getAtomLabel(i) === atomLabel) {
            const diaID = diaIDs[i];
            if (!diaIDsObject[diaID]) {
                diaIDsObject[diaID] = {
                    counter: 0,
                    oclID: diaID,
                    atomLabel: moleculeWithH.getAtomLabel(i),
                    atoms: [],
                    existingAtoms: [],
                    heavyAtoms: [],
                    attachedHydrogens: [],
                    customLabels: [],
                    attachedHydrogensCustomLabels: [],
                    heavyAtomsCustomLabels: [],
                };
            }
            const customLabel = moleculeWithH
                .getAtomCustomLabel(i)
                ?.replace(/^\]/, '');
            if (customLabel &&
                !diaIDsObject[diaID].customLabels.includes(customLabel)) {
                diaIDsObject[diaID].customLabels.push(customLabel);
            }
            if (moleculeWithH.getAtomicNo(i) === 1) {
                const connected = moleculeWithH.getConnAtom(i, 0);
                if (!diaIDsObject[diaID].heavyAtoms.includes(connected)) {
                    diaIDsObject[diaID].heavyAtoms.push(connected);
                }
                const heavyAtomCustomLabel = molecule
                    .getAtomCustomLabel(connected)
                    ?.replace(/^\]/, '');
                if (heavyAtomCustomLabel &&
                    !diaIDsObject[diaID].heavyAtomsCustomLabels.includes(heavyAtomCustomLabel)) {
                    diaIDsObject[diaID].heavyAtomsCustomLabels.push(heavyAtomCustomLabel);
                }
                if (molecule.getAtomicNo(i)) {
                    diaIDsObject[diaID].existingAtoms.push(i);
                }
                else if (!diaIDsObject[diaID].existingAtoms.includes(connected)) {
                    diaIDsObject[diaID].existingAtoms.push(connected);
                }
            }
            else {
                for (let j = 0; j < moleculeWithH.getAllConnAtoms(i); j++) {
                    const connected = moleculeWithH.getConnAtom(i, j);
                    if (moleculeWithH.getAtomicNo(connected) === 1) {
                        const attachedHydrogenCustomLabel = molecule
                            .getAtomCustomLabel(connected)
                            ?.replace(/^\]/, '');
                        if (attachedHydrogenCustomLabel &&
                            !diaIDsObject[diaID].attachedHydrogensCustomLabels.includes(attachedHydrogenCustomLabel)) {
                            diaIDsObject[diaID].attachedHydrogensCustomLabels.push(attachedHydrogenCustomLabel);
                        }
                        diaIDsObject[diaID].attachedHydrogens.push(connected);
                    }
                }
                diaIDsObject[diaID].existingAtoms.push(i);
            }
            diaIDsObject[diaID].counter++;
            diaIDsObject[diaID].atoms.push(i);
        }
    }
    for (const diaIDObject of Object.values(diaIDsObject)) {
        diaIDObject.existingAtoms.sort((a, b) => a - b);
        diaIDObject.attachedHydrogens.sort((a, b) => a - b);
    }
    return diaIDsObject;
}
//# sourceMappingURL=TopicMolecule.js.map